// @flow
import * as React from 'react';

/** A stop-gap for a common problem with localization: styled translations need to be set with inner HTML, which makes it difficult to attach event handlers to the styled portions */

// These props are an enumeration of the different event listeners that can be used. 99% of all use cases are covered by onClick, but feel free to add if it is missing something
type EventListeners = {|
  // A callback which returns the event. The index can be used to locate a specific inner DOM element if there are many (in pre-order traversal).
  // e.g. if you have <span><a>foo</a><div>bar</div>/span>, the span's onClick will invoke onClick with index 0, while a's onClick will invoke onClick with an index of 1
  // Alternatively, you can distinguish elements through attributes directly through the event (e.g. class names, id, data attributes)
  // Note that this will not respect React's event bubbling!
  onClick?: (MouseEvent, number) => mixed,
|};

const flattenNodes = (root: HTMLElement): HTMLElement[] =>
  [...root.children].reduce(
    (accum, node) => {
      accum.push(...flattenNodes(node));
      return accum;
    },
    [root],
  );

/**
 * @param EventListeners - An object describing the various decorating handlers available
 * @param key If the attached element change, then the key should also change
 * @returns A ref object
 */
const useInnerHTMLEventListeners = <T>(
  { onClick }: EventListeners,
  key: mixed,
): { current: T | null } => {
  const ref = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    if (ref.current && onClick) {
      const nodes = flattenNodes(ref.current);
      const handlers = nodes.map((node, index) => (event: MouseEvent) => {
        onClick(event, index);
      });
      for (let index = 0; index < nodes.length; index++) {
        const node = nodes[index];
        const handler = handlers[index];
        node.addEventListener('click', handler);
      }
      return () => {
        for (let index = 0; index < nodes.length; index++) {
          const node = nodes[index];
          const handler = handlers[index];
          node.removeEventListener('click', handler);
        }
      };
    }
    return () => {};
  }, [onClick, key]);
  return (ref: any);
};

export default useInnerHTMLEventListeners;
